import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { Avatar, Button, Flex, Text } from '@/components/common';
import { AccountSubpageShell } from '@/components/layout';
import { useAuth } from '@/hooks/useAuth';
import { useBlocks } from '@/hooks/useBlocks';
import { useUser } from '@/hooks/useUser';
import { cn } from '@/lib/utils';
import { calculateAge } from '@/types';
import { firstPhotoUrl } from '@/utils';

function BlockedMemberRow({
  uid,
  className,
  onUnblock,
  unblocking,
}: {
  uid: string;
  className?: string;
  onUnblock: (uid: string) => void;
  unblocking: boolean;
}) {
  const { t } = useTranslation(['settings', 'profile']);
  const { user, isLoading } = useUser(uid);

  if (isLoading && !user) {
    return (
      <Flex align="center" gap="3" className={cn('px-4 py-3', className)}>
        <Text size="1" color="gray">
          …
        </Text>
      </Flex>
    );
  }

  const name = user?.name ?? '?';
  const age = user?.age ?? calculateAge(user?.birthday);

  return (
    <Flex align="center" gap="3" className={cn('px-4 py-3', className)}>
      <Avatar name={name} src={firstPhotoUrl(user?.photos)} size="3" radius="full" />
      <Text size="2" className="min-w-0 flex-1" truncate wrap="nowrap">
        {t('profile:account.nameAge', { name, age })}
      </Text>
      <Button
        type="button"
        variant="outline"
        color="gray"
        highContrast
        radius="full"
        size="sm"
        disabled={unblocking}
        isLoading={unblocking}
        onClick={() => onUnblock(uid)}
      >
        {t('settings:blocked.unblock')}
      </Button>
    </Flex>
  );
}

function BlockedMembersList() {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { blockedUids, isLoading, unblockUser } = useBlocks(user);
  const [unblockingUid, setUnblockingUid] = useState<string | null>(null);

  const handleUnblock = async (blockedUid: string) => {
    setUnblockingUid(blockedUid);
    try {
      await unblockUser(blockedUid);
    } catch (err) {
      console.error(err);
    } finally {
      setUnblockingUid(null);
    }
  };

  if (isLoading) return null;

  if (blockedUids.length === 0) {
    return (
      <Text size="1" color="gray" align="center" mt="6">
        {t('blocked.empty')}
      </Text>
    );
  }

  return (
    <div className="overflow-hidden rounded-btn border border-white/15 bg-black">
      {blockedUids.map((uid, index) => (
        <BlockedMemberRow
          key={uid}
          uid={uid}
          className={index < blockedUids.length - 1 ? 'border-b border-white/8' : undefined}
          onUnblock={uid => void handleUnblock(uid)}
          unblocking={unblockingUid === uid}
        />
      ))}
    </div>
  );
}

export function BlockedMembersPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();

  return (
    <AccountSubpageShell
      onBack={() => navigate('/account/settings')}
      title={t('blocked.title')}
      subtitle={t('blocked.subtitle')}
    >
      <BlockedMembersList />
    </AccountSubpageShell>
  );
}
